/* Colors */
export const grey2 = "#f7f8f8";
export const grey3 = "#eff0f0";
export const grey5 = "#e0e2e2";
export const grey10 = "#d0d3d3";
export const grey15 = "#bdc0c0";
export const grey20 = "#a9adad";
export const grey25 = "#9da1a1";
export const grey30 = "#878a8c";
export const grey35 = "#676b6e";
export const grey40 = "#53575b";
export const grey60 = "#3a3d40";
export const grey90 = "#0D0F10";
export const yellow5 = "#ffd61a";
export const yellow10 = "#ffb81c";
export const yellow15 = "#faa61a";
export const yellow20 = "#f68d2e";
export const yellow25 = "#e66a1f";
export const yellow30 = "#d45311";
export const yellow35 = "#974a07";
export const yellow40 = "#674730";
export const red5 = "#fda1b2";
// Exception where we need to have a different color for light vs dark due to accessibility contrast on our normally "fixed" color values.
export const red10 = setAppearanceConstantGlobal("red10", {
  light: "#fc4e6d",
  dark: "#FF7591"
});
export const red15 = setAppearanceConstantGlobal("red15", {
  light: "#e10f5a",
  dark: "#ED3A65"
});
export const red20 = "#bd0057";
export const red25 = "#a6004e";
export const red30 = "#8f124a";
export const red35 = "#7d003f";
export const red40 = "#6e0d33";
export const blue5 = "#b0eefc";
export const blue10 = "#7de3f4";
export const blue15 = "#49cce6";
export const blue20 = "#01b6d1";
export const blue25 = "#007da5";
export const blue30 = "#006184";
export const blue35 = "#005175";
export const blue40 = "#003057";
export const green5 = "#d3e952";
export const green10 = "#bed21e";
export const green15 = "#93c742";
export const green20 = "#6db344";
export const green25 = "#50a83e";
export const green30 = "#318d43";
export const green35 = "#206b3f";
export const green40 = "#235c35";
export const text120 = "#212225";
export const black = "#000000";
export const white = "#ffffff";
export const colorFacebook = "#3b5998";
export const colorInstagram = "#3c5a96";
export const colorTwitter = "#00b6f1";
export const colorPinterest = "#cb2027";
export const colorYoutube = "#cd201f";
export const colorOdnok = "#ed812b";
export const colorVk = "#45668e";

/* Font families */
// The Android fonts (Roboto, Noto Serif) need to come before other fallbacks (Arial, Georgia) because Android aliases those names to the Android fonts and the fallback metric adjustments won't correctly apply (See Fonts 2.1.0)
export const sans = `"Ensign:Sans", Roboto, Arial, sans-serif`;
export const serif = `"Ensign:Serif", "Noto Serif", Georgia, serif`;
export const monospace = `Courier, "Courier New", monospace`;

/* Font Weights */
export const fontWeightLight = 300;
export const fontWeightNormal = 400;
export const fontWeightSemiBold = 600;
export const fontWeightBold = 700;

/* Font sizes */
export const fontSizeRoot = `100%`; // effectively 16px (16px * 100% = 16px) but respects users' special needs, then desired px / 16px (root size) = rem
export const fontSize11 = "0.6875rem";
export const fontSize13 = "0.8125rem";
export const fontSize14 = "0.875rem";
export const fontSize16 = "1rem";
export const fontSize18 = "1.125rem";
export const fontSize20 = "1.25rem";
export const fontSize28 = "1.75rem";
export const fontSize32 = "2rem";
export const fontSize42 = "2.625rem";

/* Line heights */
export const lineHeightTight = 1.2;
export const lineHeight = 1.4;
export const lineHeightLoose = 1.6;

/* Spacings */
export const spacing4 = "0.25rem"; // 4px
export const spacing8 = "0.5rem"; // 8px
export const spacing16 = "1rem"; // 16px
export const spacing24 = "1.5rem"; // 24px
export const spacing32 = "2rem"; // 32px
export const spacing48 = "3rem"; // 48px
export const spacing64 = "4rem"; // 64px
export const spacing96 = "6rem"; // 96px
export const spacing128 = "8rem"; // 128px

/* Breakpoints */
export const breakWidth480 = "480px";
export const breakWidth600 = "600px";
export const breakWidth840 = "840px";
export const breakWidth960 = "960px";
export const breakWidth1280 = "1280px";
export const breakWidth1440 = "1440px";
export const breakWidth1600 = "1600px";

/* Aspect ratios */
export const aspectRatio1By1 = {
  width: 1,
  height: 1,
  key: "1:1"
};
export const aspectRatio5By6 = {
  width: 5,
  height: 6,
  key: "5:6"
};
export const aspectRatio5By8 = {
  width: 5,
  height: 8,
  key: "5:8"
};
export const aspectRatio6By5 = {
  width: 6,
  height: 5,
  key: "6:5"
};
export const aspectRatio8By3 = {
  width: 8,
  height: 3,
  key: "8:3"
};
export const aspectRatio8By5 = {
  width: 8,
  height: 5,
  key: "8:5"
};
export const aspectRatio16By9 = {
  width: 16,
  height: 9,
  key: "16:9"
};
export const aspectRatios = [aspectRatio1By1, aspectRatio5By6, aspectRatio5By8, aspectRatio6By5, aspectRatio8By3, aspectRatio8By5, aspectRatio16By9];

/* Z indexes */
export const zIndexHigherThanPlatform = 200;

/* Border radius */
export const borderRadius = "0.125rem"; // 2px

/* White transparencies (used for dark mode) - comment of where they came from is current naming in Figma. In time they should update to match ours */
export const whiteTransparency10 = "#FFFFFFDE"; // Figma: Dark Mode/Transparency/87%
export const whiteTransparency20 = "#FFFFFF99"; // Figma: Dark Mode/Transparency/60%
export const whiteTransparency30 = "#FFFFFF61"; // Figma: Dark Mode/Transparency/38%
export const whiteTransparency40 = "#FFFFFF3D"; // Figma: Dark Mode/Transparency/24%
export const whiteTransparency50 = "#FFFFFF33"; // Figma: Dark Mode/Transparency/20%
export const whiteTransparency60 = "#FFFFFF29"; // Figma: Dark Mode/Transparency/16%
export const whiteTransparency70 = "#FFFFFF1F"; // Figma: Dark Mode/Transparency/12%
export const whiteTransparency80 = "#FFFFFF14"; // Figma: Dark Mode/Transparency/8%

/**
 *
 * Appearance Constants
 *
 */

import { setAppearanceConstantGlobal } from "./modes";
export { setAppearanceConstant, getAppearanceConstant, setAppearanceConstantLocal } from "./modes";

// Backgrounds
export const backgroundColorAlt = setAppearanceConstantGlobal("background-color-alt", {
  light: grey3,
  dark: whiteTransparency30
});
export const backgroundColorHover = setAppearanceConstantGlobal("background-color-hover", {
  light: grey3,
  dark: whiteTransparency60
});
export const backgroundColorFocused = setAppearanceConstantGlobal("background-color-focused", {
  light: grey5,
  dark: whiteTransparency40
});
export const backgroundColorDisabled = setAppearanceConstantGlobal("background-color-disabled", {
  light: grey2,
  dark: whiteTransparency70
});

// This is somewhat dynamically updated by code at the bottom of normalize. This tries to match the current level of the background, which changes on dark mode. Some child components will need to reflect the current background level of their parent which this facilitates.
export const backgroundColorLevelCurrent = setAppearanceConstantGlobal("background-color-level-current", {
  light: white,
  dark: "#121212"
});
export const backgroundColorLevel1 = setAppearanceConstantGlobal("background-color-level1", {
  light: white,
  dark: "#121212"
});
export const backgroundColorLevel2 = setAppearanceConstantGlobal("background-color-level2", {
  light: white,
  // 8% white on #121212
  dark: "#252525"
});
export const backgroundColorLevel3 = setAppearanceConstantGlobal("background-color-level3", {
  light: white,
  // 11% white on #121212
  dark: "#2c2c2c"
});
export const backgroundColorLevel4 = setAppearanceConstantGlobal("background-color-level4", {
  light: white,
  // 14% white on #121212
  dark: "#333333"
});
export const backgroundColorLevel5 = setAppearanceConstantGlobal("background-color-level5", {
  light: white,
  // 16% white on #121212
  dark: "#383838"
});

// text/icon
export const textColorPrimary = setAppearanceConstantGlobal("text-color-primary", {
  light: text120,
  dark: whiteTransparency10
});
export const textColorSecondary = setAppearanceConstantGlobal("text-color-secondary", {
  light: grey40,
  dark: whiteTransparency20
});
export const textColorTertiary = setAppearanceConstantGlobal("text-color-tertiary", {
  light: grey30,
  dark: whiteTransparency30
});
export const textColorInverted = setAppearanceConstantGlobal("text-color-inverted", {
  light: white,
  dark: black
});

// accents
export const accentColorLink = setAppearanceConstantGlobal("accent-color-link", {
  light: "#157493",
  dark: blue15
});
export const accentColorLinkActive = setAppearanceConstantGlobal("accent-color-link-active", {
  light: "#b00504",
  dark: "#FF7770"
});
export const accentColorLinkVisited = setAppearanceConstantGlobal("accent-color-link-visited", {
  light: "#9C4F9C",
  dark: "#C092FC"
});
export const accentColorPrimary = setAppearanceConstantGlobal("accent-color-primary", {
  light: blue30,
  dark: blue15
});
export const accentColorInfo = setAppearanceConstantGlobal("accent-color-info", {
  light: "#1E9FC8",
  dark: blue15
});
export const accentColorConfirmation = setAppearanceConstantGlobal("accent-color-confirmation", {
  light: "#64A53D",
  dark: green15
});
export const accentColorWarning = setAppearanceConstantGlobal("accent-color-warning", {
  light: "#EC7000",
  dark: yellow15
});
export const accentColorDanger = setAppearanceConstantGlobal("accent-color-danger", {
  light: "#b00504",
  dark: "#ff7770"
});

// Borders
export const borderColorPrimary = setAppearanceConstantGlobal("border-color-primary", {
  light: grey30,
  dark: whiteTransparency20
});
export const borderColorSecondary = setAppearanceConstantGlobal("border-color-secondary", {
  light: grey15,
  dark: whiteTransparency30
});
export const borderColorTertiary = setAppearanceConstantGlobal("border-color-tertiary", {
  light: grey5,
  dark: whiteTransparency70
});

// highlights

export const highlightRed = setAppearanceConstantGlobal("highlight-red", {
  light: "#FE4F6633",
  dark: "#BA505E61"
});
export const highlightOrange = setAppearanceConstantGlobal("highlight-orange", {
  light: "#FE98293D",
  dark: "#C68A4E61"
});
export const highlightYellow = setAppearanceConstantGlobal("highlight-yellow", {
  light: "#FCDB3B61",
  dark: "#D6BF5961"
});
export const highlightGreen = setAppearanceConstantGlobal("highlight-green", {
  light: "#A9D5273D",
  dark: "#8CA05261"
});
export const highlightBlue = setAppearanceConstantGlobal("highlight-blue", {
  light: "#10D9E133",
  dark: "#60B5B861"
});
export const highlightDarkBlue = setAppearanceConstantGlobal("highlight-darkblue", {
  light: "#2596FF29",
  dark: "#5587B561"
});
export const highlightPurple = setAppearanceConstantGlobal("highlight-purple", {
  light: "#9D53FE29",
  dark: "#7654A561"
});
export const highlightPink = setAppearanceConstantGlobal("highlight-pink", {
  light: "#F85BEA33",
  dark: "#B56BB04D"
});
export const highlightBrown = setAppearanceConstantGlobal("highlight-brown", {
  light: "#CD7D5A33",
  dark: "#946D5D61"
});
export const highlightGrey = setAppearanceConstantGlobal("highlight-grey", {
  light: "#AEB8C13D",
  dark: "#8E969C61"
});

// box shadows
export const boxShadowRaised = setAppearanceConstantGlobal("box-shadow-raised", {
  light: "0 2px 4px 0 #00000033",
  // 20%
  dark: "0 2px 4px 0 #00000066" // 40%
});

export const boxShadowDetached = setAppearanceConstantGlobal("box-shadow-detached", {
  light: "0 4px 8px 0 #00000033",
  dark: "0 4px 8px 0 #00000066"
});
export const boxShadowOverlaid = setAppearanceConstantGlobal("box-shadow-overlaid", {
  light: "0 8px 16px 0 #00000033",
  dark: "0 8px 16px 0 #00000066"
});
export const boxShadowPoppedOut = setAppearanceConstantGlobal("box-shadow-popped", {
  light: "0 16px 32px 0 #00000033",
  dark: "0 16px 32px 0 #00000066"
});